import { Dictation, RawTranscription } from '../_interfaces/dictation';
import { FrontendResult } from '../_interfaces/frontend-result';
import { TranscriptUtterance, Utterance } from '../_interfaces/utterance';

export type { TranscriptUtterance } from '../_interfaces/utterance';

export function formatTranscriptTime(offsetMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, offsetMs) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/** Live WS results expose `start` in seconds; utterance API uses `offset` in ms. */
export function frontendResultStartToOffsetMs(start: number | undefined | null): number {
  if (start == null || !Number.isFinite(start)) return 0;
  return Math.round(start * 1000);
}

export function mapUtterancesToTranscript(raw: Utterance[]): TranscriptUtterance[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  return [...raw]
    .sort((a, b) => (a.uttIdx ?? 0) - (b.uttIdx ?? 0) || (a.offset ?? 0) - (b.offset ?? 0))
    .map((u) => ({
      offset: u.offset ?? 0,
      text: (u.alternatives?.[0] ?? '').trim(),
    }))
    .filter((u) => u.text.length > 0);
}

/** Note generation `content` item. Kept in NLP response order, unlike `mapUtterancesToTranscript`. */
export interface GenerationUtterance {
  uttIdx: number;
  speaker?: string;
  text: string;
}

export function mapUtterancesToGenerationContent(raw: Utterance[]): GenerationUtterance[] {
  if (raw.length === 0) return [];

  const content: GenerationUtterance[] = [];
  for (const utterance of raw) {
    const text = (utterance.alternatives?.[0] ?? '').trim();
    if (!text) continue;

    const mapped: GenerationUtterance = { uttIdx: utterance.uttIdx, text };
    if (utterance.speakerTag != null) {
      mapped.speaker = `Speaker ${utterance.speakerTag}`;
    }
    content.push(mapped);
  }
  return content;
}

export function buildTranscriptionFromRawTranscription(
  rawTranscription: RawTranscription | null | undefined
): string {
  const words = rawTranscription?.words;
  if (!Array.isArray(words) || words.length === 0) return '';

  return [...words]
    .sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
    .map((entry) => entry.word?.trim() ?? '')
    .filter((word) => word.length > 0)
    .join(' ')
    .trim();
}

export function getDictationTranscription(dictation: Dictation | null | undefined): string {
  const isLegacy = !dictation?.copilot?.type;
  const stored = dictation?.copilot?.transcription?.trim() ?? '';
  if (isLegacy) {
    return stored;
  }
  return buildTranscriptionFromRawTranscription(dictation.rawTranscription) || stored;
}

export function hasDictationTranscription(dictation: Dictation | null | undefined): boolean {
  return getDictationTranscription(dictation).length > 0;
}

export interface LiveTranscriptState {
  utterances: TranscriptUtterance[];
  activeIndex: number | null;
}

function setTranscriptPreview(
  state: LiveTranscriptState,
  utterance: TranscriptUtterance,
): LiveTranscriptState {
  const { utterances, activeIndex } = state;

  if (activeIndex === null) {
    return {
      utterances: [...utterances, utterance],
      activeIndex: utterances.length,
    };
  }

  return {
    utterances: utterances.map((current, index) =>
      index === activeIndex ? utterance : current
    ),
    activeIndex,
  };
}

function removeTranscriptPreview(state: LiveTranscriptState): LiveTranscriptState {
  const { utterances, activeIndex } = state;
  if (activeIndex === null) return state;

  return {
    utterances: utterances.filter((_, index) => index !== activeIndex),
    activeIndex: null,
  };
}

export function applyLiveTranscriptResult(
  state: LiveTranscriptState,
  result: FrontendResult,
): LiveTranscriptState & { finalChunk: string | null } {
  const isFinal = result.final;
  const chunk = Array.isArray(result.words) ? (result.words[0] ?? '').trim() : '';
  const hasChunk = Boolean(chunk);

  let nextState = state;

  if (hasChunk) {
    const utterance: TranscriptUtterance = {
      offset: frontendResultStartToOffsetMs(result.start),
      text: chunk,
      final: isFinal,
    };
    nextState = setTranscriptPreview(nextState, utterance);
  } else if (isFinal) {
    nextState = removeTranscriptPreview(nextState);
  }

  if (!isFinal) {
    return { ...nextState, finalChunk: null };
  }

  return {
    ...nextState,
    activeIndex: null,
    finalChunk: hasChunk ? chunk : null,
  };
}
